import { type Toast, useToastStore } from '@/stores/toast.store';
import { cn } from '@/lib/utils';

const VARIANT_STYLES: Record<
  NonNullable<Toast['variant']>,
  { border: string; icon: React.ReactNode }
> = {
  success: {
    border: 'border-polar-green-3',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" fill="#f6ffed" stroke="#52c41a" />
        <path
          d="M6 10.2l2.6 2.6L14 7.4"
          stroke="#52c41a"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  error: {
    border: 'border-[#ffccc7]',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" fill="#fff2f0" stroke="#f5222d" />
        <path
          d="M7 7l6 6M13 7l-6 6"
          stroke="#f5222d"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  warning: {
    border: 'border-calendula-gold-3',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" fill="#fffbe6" stroke="#faad14" />
        <path
          d="M10 5.5v5.5"
          stroke="#faad14"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="10" cy="14" r="0.9" fill="#faad14" />
      </svg>
    ),
  },
  info: {
    border: 'border-[#d3adf7]',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" fill="#f6f0ff" stroke="#7956d8" />
        <path
          d="M10 9v5"
          stroke="#7956d8"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="10" cy="6.5" r="0.9" fill="#7956d8" />
      </svg>
    ),
  },
  default: {
    border: 'border-neutral-100',
    icon: null,
  },
};

/**
 * Renders the queued toasts from `useToastStore` (bottom-right stack).
 * Purely presentational — the store owns the auto-dismiss timers, this
 * component only lists active toasts and offers manual dismiss/action.
 * Mounted once at the app root.
 */
export function ToastViewport() {
  const toasts = useToastStore(s => s.toasts);
  const removeToast = useToastStore(s => s.removeToast);

  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      className="fixed bottom-6 right-6 z-[100] flex w-[360px] max-w-[calc(100vw-3rem)] flex-col gap-3"
    >
      {toasts.map(toast => {
        const style = VARIANT_STYLES[toast.variant ?? 'default'];
        return (
          <div
            key={toast.id}
            role="status"
            className={cn(
              'flex items-start gap-3 rounded-lg border bg-white p-4 shadow-[0_6px_16px_0_rgba(0,0,0,0.08)]',
              style.border
            )}
          >
            {style.icon && (
              <span className="mt-0.5 shrink-0">{style.icon}</span>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-roboto text-sm font-medium leading-[22px] text-character-title-85">
                {toast.title}
              </p>
              {toast.description && (
                <p className="mt-0.5 font-roboto text-sm leading-[22px] text-neutral-7">
                  {toast.description}
                </p>
              )}
              {toast.action && (
                <button
                  type="button"
                  onClick={() => {
                    toast.action?.onClick();
                    removeToast(toast.id);
                  }}
                  className="mt-2 font-roboto text-sm font-medium text-primary-6 hover:underline"
                >
                  {toast.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              aria-label="Dismiss notification"
              onClick={() => removeToast(toast.id)}
              className="shrink-0 rounded p-0.5 text-neutral-7 transition-colors hover:text-character-title-85"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M3.5 3.5l7 7M10.5 3.5l-7 7"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
}
